import React from 'react';
import { FORM_STEP_CLASS, getPageTransitionClasses } from '../Util/pageTransition';
import { useScrollFade } from '../hooks/useScrollFade';
import { useStepFocus } from '../hooks/useStepFocus';
import { formCardClass } from '../Util/formHeightUtils';

function FormPageShell({
  page,
  pageName,
  direction = null,
  /** Event forms take the event page floor (formCardClass). */
  isEvent = false,
  isDrawerOpen,
  isHostedPage,
  formDisplayMode,
  fixedFormElementHeight,
  fixedFormElementStyles,
  disableFixedHeightScroll = false,
  children,
  faq = null,
  drawerInnerPadding = '10px',
  usePageContainer = true,
}) {
  const isOverlay = formDisplayMode === 'overlay';
  const wrapperClassName = `${isHostedPage && !isDrawerOpen ? 'hosted-spacing-wrapper' : !isHostedPage && !isOverlay && !isDrawerOpen ? 'dynamic-spacing-wrapper' : ''} ${isOverlay ? 'flex-1 flex flex-col' : ''}`;
  // CON-529 F9: cards hug their content, except the question step. Its
  // multi-select menu is portaled with `position: fixed` and can stay open after
  // Tab; in a hugging inline iframe (~360px) that menu landed on the Support
  // button (EMB-011 "menus inline"). That step keeps the height floor.
  // The card, with the page floor in the embed and the hosted page (formCardClass).
  const innerClassName = formCardClass({ isDrawerOpen, isHostedPage, isOverlay, isEvent });
  const isActive = page === pageName;
  const stepRef = useStepFocus(isActive);
  // This inner div is a scroll container (overflowY:auto) only when a fixed
  // height is set and internal scrolling isn't disabled (embed / hosted desktop
  // fixed-height mode). The drawer/dynamic modes scroll in their own parent.
  const isScrollFadeContainer = Boolean(fixedFormElementHeight && !disableFixedHeightScroll);
  // Scroll-aware edge fade: toggles data-fade-top/-bottom on the scroll node
  // only when content is actually scrolled past that edge (so no fade at the
  // top, and none when content fits). No-ops when ref is null / not scrollable.
  const scrollFadeRef = useScrollFade(isScrollFadeContainer);
  const shouldStretchTransitionWrapper = Boolean(
    fixedFormElementHeight || fixedFormElementStyles?.height || isDrawerOpen || isOverlay,
  );
  const transitionClassName = getPageTransitionClasses(isActive, direction, formDisplayMode);
  const transitionWrapperStyle = {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    width: '100%',
  };
  // The step's content carries the slide (FORM_STEP_CLASS): each step page applies the
  // transition class to its own content wrapper, below its title strip.
  const pageChildren = React.isValidElement(children)
    ? React.cloneElement(children, {
        transitionClassName,
        transitionWrapperStyle,
        isPageActive: isActive,
        pageTransitionDirection: direction,
      })
    : children;

  const pageMarkup = (
    <div
      ref={stepRef}
      className={`page ${FORM_STEP_CLASS} ${isActive ? 'active' : ''} ${!isDrawerOpen && !isHostedPage && !isOverlay ? 'container' : ''} ${!isDrawerOpen && !isOverlay ? 'mx-auto' : ''} h-full items-center ${isDrawerOpen ? 'w-full px-1' : isHostedPage ? 'max-w-lg px-3' : isOverlay ? 'w-full' : 'max-w-md'} iframe-content-container page-transition-container`.trim()}
    >
      <div
        className={wrapperClassName}
        style={{
          ...(!isHostedPage || isDrawerOpen
            ? { padding: isDrawerOpen ? '16px 0' : isOverlay ? '0' : '24px 0' }
            : {}),
          margin: '0',
          height: isDrawerOpen ? '100%' : 'auto',
          boxSizing: 'border-box',
        }}
      >
        <div
          ref={isScrollFadeContainer ? scrollFadeRef : undefined}
          className={`${innerClassName} ${isScrollFadeContainer ? 'scroll-fade-container' : ''}`.trim()}
          style={{
            display: 'flex',
            flexDirection: 'column',
            height:
              isDrawerOpen
                ? '100%'
                : isOverlay
                  ? 'auto'
                  : fixedFormElementHeight || 'auto',
            ...fixedFormElementStyles,
            ...(isScrollFadeContainer && {
              overflowY: 'auto',
            }),
            ...(isDrawerOpen && {
              margin: '0',
              borderRadius: '0',
              ...(drawerInnerPadding ? { padding: drawerInnerPadding } : {}),
            }),
          }}
        >
          <div
            className={`flex flex-col flex-1 ${
              isDrawerOpen ? 'bg-white h-full rounded-none' : ''
            }`}
            style={{
              height: isDrawerOpen ? '100%' : undefined,
              minHeight: 0,
              padding: isDrawerOpen ? '0' : undefined,
            }}
          >
            <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {pageChildren}
            </div>
          </div>
        </div>
        {faq}
      </div>
    </div>
  );

  if (!usePageContainer) {
    return pageMarkup;
  }

  return (
    <div
      className="page-container"
      data-mode={isOverlay ? 'overlay' : isDrawerOpen ? 'drawer' : 'dynamic'}
      style={{
        minHeight: isDrawerOpen ? '400px' : 'fit-content',
        height: isDrawerOpen ? '100%' : 'auto',
      }}
    >
      {pageMarkup}
    </div>
  );
}

export default FormPageShell;
